import React, { ReactNode } from 'react';
import { ArrowUpRight, ArrowDownRight, Minus, LoaderCircle, ChevronRight, TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { cn } from '../../../../lib/utils';
import { StatCard } from '../../../ui/StatCard';
import { iconBgRed, iconBgGreen, iconBgBlue } from '../../../../styling/stylingHelper';
import { useChartTheme } from '../../../../utils/chartTheme';

export { useChartTheme };

/**
 * Shared UI primitives for the report components in this folder. Every report
 * uses the same cards, KPIs, tables, badges and formatting helpers — keep them
 * here instead of copy-pasting per report. The visual language mirrors the rest
 * of the web app (cards on the darker page background, the order-history table
 * style, the SimpleCard KPI style) so the reports feel native.
 */

/** Format a signed percentage like "+12.3%" / "-4.0%". */
export const pct = (value: number): string => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;

/** Format a percentage figure like "31.4%". */
export const pctAbs = (value: number, decimals = 1): string => `${value.toFixed(decimals)}%`;

/** Small up/down/flat change badge, green for "good", red for "bad". */
export const ChangeBadge: React.FC<{ value: number; goodWhenUp?: boolean; suffix?: string; hidden?: boolean }> = ({
  value,
  goodWhenUp = true,
  suffix = '%',
  hidden = false,
}) => {
  if (hidden) return <span className="text-xs text-muted dark:text-muted-dark">—</span>;
  const isUp = value > 0.05;
  const isDown = value < -0.05;
  const positive = isUp ? goodWhenUp : isDown ? !goodWhenUp : true;
  const color = !isUp && !isDown
    ? 'text-muted dark:text-muted-dark'
    : positive
    ? 'text-textGreen dark:text-textGreen-dark'
    : 'text-textRed dark:text-textRed-dark';
  const Icon = isUp ? ArrowUpRight : isDown ? ArrowDownRight : Minus;
  const text = suffix === '%' ? pct(value) : `${value >= 0 ? '+' : ''}${value.toFixed(1)}${suffix}`;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3" />
      {text}
    </span>
  );
};

/** Ranked-list badge — the gradient circle used by the order/revenue history
 * item pages and the weekly report email (1 = green, 2 = purple, 3 = amber,
 * 4+ = grey). */
export const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  const background =
    rank === 1
      ? 'linear-gradient(180deg, #0E8965 0%, #4AC4A0 100%)'
      : rank === 2
      ? 'linear-gradient(180deg, #7F56D9 0%, #9F73FF 100%)'
      : rank === 3
      ? 'linear-gradient(180deg, #F4B857 0%, #F48357 100%)'
      : 'linear-gradient(180deg, #8C97A6 0%, #818A97 100%)';
  return (
    <div
      className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] leading-[14px] tracking-[0.1px] font-bold flex-shrink-0"
      style={{ background }}
    >
      {rank}
    </div>
  );
};

/**
 * In-report breadcrumb for drill-down navigation (e.g. "All suppliers › Hanos").
 * The report detail pages live inside `ReportDetailLayout`, whose `TopSection`
 * already renders a back arrow to the reports hub — so a second arrow-style "back"
 * button inside the report reads as a confusing double back button. This
 * breadcrumb communicates the in-report hierarchy instead: earlier crumbs are
 * clickable to step back up a level, the last crumb is the current view.
 */
export const ReportBreadcrumb: React.FC<{ items: Array<{ label: string; onClick?: () => void }> }> = ({ items }) => (
  <nav className="flex items-center gap-1.5 text-sm mb-4 flex-wrap">
    {items.map((item, i) => (
      <React.Fragment key={i}>
        {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted dark:text-muted-dark shrink-0" />}
        {item.onClick ? (
          <button
            onClick={item.onClick}
            className="text-muted dark:text-muted-dark hover:text-textPrimary dark:hover:text-textPrimary-dark transition-colors"
          >
            {item.label}
          </button>
        ) : (
          <span className="font-medium text-textPrimary dark:text-textPrimary-dark">{item.label}</span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

/** A report content card — matches the app card style (white card on the darker
 * page background, rounded, subtle border + shadow). */
export const SectionCard: React.FC<{ title?: string; subtitle?: string; actions?: ReactNode; children: React.ReactNode; className?: string }> = ({
  title,
  subtitle,
  actions,
  children,
  className,
}) => (
  <div
    className={cn(
      'bg-background dark:bg-background-dark border border-border dark:border-border-dark rounded-xl shadow-sm p-5 mb-6',
      className
    )}
  >
    {(title || actions) && (
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          {title && <h2 className="text-lg font-semibold text-textPrimary dark:text-textPrimary-dark">{title}</h2>}
          {subtitle && <p className="text-xs text-muted dark:text-muted-dark mt-0.5">{subtitle}</p>}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
    )}
    {children}
  </div>
);

export const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <p className="text-sm text-muted dark:text-muted-dark py-2">{message}</p>
);

/** Full-card empty / unavailable state (replaces the bespoke per-report cards). */
export const EmptyCard: React.FC<{ message: string; icon?: React.ElementType }> = ({ message, icon: Icon }) => (
  <div className="bg-background dark:bg-background-dark border border-border dark:border-border-dark rounded-xl shadow-sm p-8 text-center">
    {Icon && <Icon className="w-8 h-8 text-muted dark:text-muted-dark mx-auto mb-2" />}
    <p className="text-muted dark:text-muted-dark">{message}</p>
  </div>
);

/** A single KPI tile, rendered via the unified app `StatCard`. `tone` picks the
 * icon + colored tile (up = red / down = green / neutral = blue); `hint` shows
 * as a small muted line under the value. */
export const Kpi: React.FC<{ label: string; value: string; hint?: string; tone?: 'up' | 'down' | 'neutral' }> = ({
  label,
  value,
  hint,
  tone = 'neutral',
}) => {
  const { icon, iconBg } =
    tone === 'up'
      ? { icon: <TrendingUp className="w-6 h-6" style={{ color: '#D92D20' }} />, iconBg: iconBgRed }
      : tone === 'down'
      ? { icon: <TrendingDown className="w-6 h-6" style={{ color: '#079455' }} />, iconBg: iconBgGreen }
      : { icon: <BarChart3 className="w-6 h-6" style={{ color: '#2E90FA' }} />, iconBg: iconBgBlue };
  return (
    <StatCard
      icon={icon}
      iconBg={iconBg}
      title={label}
      value={value}
      bottom={hint ? <span className="text-xs text-muted dark:text-muted-dark">{hint}</span> : undefined}
    />
  );
};

/** Responsive grid wrapper for a row of KPI tiles. */
export const KpiGrid: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6', className)}>{children}</div>
);

/** Pill-style segmented toggle (food/drinks, group/section, …). */
export function PillToggle<T extends string>({
  options,
  value,
  onChange,
  isDisabled,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  isDisabled?: (v: T) => boolean;
}): React.ReactElement {
  return (
  <div className="flex gap-1 bg-muted/10 dark:bg-muted-dark/10 rounded-lg p-1">
    {options.map((opt) => {
      const disabled = isDisabled?.(opt.value) ?? false;
      return (
        <button
          key={opt.value}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
          className={cn(
            'px-4 py-1 rounded-md text-sm font-medium transition',
            value === opt.value
              ? 'bg-background dark:bg-background-dark shadow text-textPrimary dark:text-textPrimary-dark'
              : 'text-muted dark:text-muted-dark hover:text-textPrimary dark:hover:text-textPrimary-dark',
            disabled && 'opacity-40 cursor-not-allowed'
          )}
        >
          {opt.label}
        </button>
      );
    })}
    </div>
  );
}

/** App-consistent styled <select>. */
export const ReportSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className, ...props }) => (
  <select
    {...props}
    className={cn(
      'px-3 py-1.5 rounded-lg text-sm bg-background dark:bg-background-dark border border-border dark:border-border-dark text-textPrimary dark:text-textPrimary-dark',
      className
    )}
  />
);

/** Centered spinner shown while a report's stores are still loading. */
export const ReportLoading: React.FC = () => (
  <div className="flex items-center justify-center py-12">
    <LoaderCircle className="w-8 h-8 animate-spin text-muted dark:text-muted-dark" />
  </div>
);

// --- Charts ---------------------------------------------------------------

/** Brand colours reused across report charts (match the dashboard / charts page). */
export const CHART_GREEN = '#16B364';
export const CHART_RED = '#F04438';
export const CHART_BLUE = '#2E90FA';
export const CHART_AMBER = '#F1A62D';
export const CHART_GREY = '#9CA3AF';

/** Food/drinks split colours — kept in sync with the weekly report email
 * (`highestSpendingProductsSection.ts`) so the web report and email match. */
export const SPLIT_FOOD = '#18B081';
export const SPLIT_DRINKS = '#F4B857';

/**
 * Horizontal food/drinks split bar with a legend, mirroring the weekly email's
 * spending split (`renderSpendingSplit`). Renders nothing when the total is zero.
 */
export const SplitBar: React.FC<{
  leftLabel: string;
  rightLabel: string;
  leftValue: number;
  rightValue: number;
  format: (n: number) => string;
}> = ({ leftLabel, rightLabel, leftValue, rightValue, format }) => {
  const total = leftValue + rightValue;
  if (total <= 0) return null;
  const leftPct = (leftValue / total) * 100;
  const rightPct = (rightValue / total) * 100;
  return (
    <div className="mb-4">
      <div className="flex h-6 w-full overflow-hidden rounded-md">
        {leftPct > 0 && <div style={{ width: `${leftPct}%`, backgroundColor: SPLIT_FOOD }} />}
        {rightPct > 0 && <div style={{ width: `${rightPct}%`, backgroundColor: SPLIT_DRINKS }} />}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SPLIT_FOOD }} />
          <span className="text-muted dark:text-muted-dark font-medium">{leftLabel}</span>
          <span className="text-textPrimary dark:text-textPrimary-dark font-semibold">
            {pctAbs(leftPct)} · {format(leftValue)}
          </span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SPLIT_DRINKS }} />
          <span className="text-muted dark:text-muted-dark font-medium">{rightLabel}</span>
          <span className="text-textPrimary dark:text-textPrimary-dark font-semibold">
            {pctAbs(rightPct)} · {format(rightValue)}
          </span>
        </span>
      </div>
    </div>
  );
};

export interface BarDatum {
  label: string;
  value: number;
  color?: string;
}

/**
 * Horizontal single-series bar chart for "top products by €" style sections.
 * Pure presentation — the report passes already-sorted/labelled data and a
 * value formatter (e.g. `eur`).
 */
export const ReportBarChart: React.FC<{
  data: BarDatum[];
  valueFormatter: (n: number) => string;
  height?: number;
  defaultColor?: string;
}> = ({ data, valueFormatter, height, defaultColor = CHART_GREEN }) => {
  const theme = useChartTheme();
  return (
    <div style={{ width: '100%', height: height ?? Math.max(140, data.length * 34 + 30) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
          <XAxis type="number" tickFormatter={valueFormatter} tick={{ fill: theme.axisTick, fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="label" width={150} tick={{ fill: theme.axisTick, fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip
            formatter={(v: number) => valueFormatter(v)}
            cursor={{ fill: theme.cursorFill }}
            contentStyle={{ backgroundColor: theme.tooltipBg, borderColor: theme.tooltipBorder, color: theme.tooltipText }}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.color ?? defaultColor} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// --- Reusable table -------------------------------------------------------

export interface ReportColumn<T> {
  key: string;
  header: ReactNode;
  align?: 'left' | 'right';
  render: (row: T, index: number) => ReactNode;
  /** Extra classes for the cell (e.g. conditional colouring). */
  cellClassName?: (row: T) => string;
}

/**
 * Generic report table that reuses the exact visual language of the app's
 * order-history `TableComponent` (white header row with top/bottom border, small
 * tertiary uppercase-ish headers, hoverable rows with row dividers). Reports pass
 * their already-computed view-model rows + a column config.
 */
export function ReportTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  footer,
  rowClassName,
}: {
  columns: ReportColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  footer?: ReactNode;
  rowClassName?: (row: T) => string;
}): React.ReactElement {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-background dark:bg-background-dark border-t border-b border-grey-200 dark:border-borderPrimary-dark">
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'text-xs font-medium text-textTertiary dark:text-textTertiary-dark px-3 py-2 whitespace-nowrap',
                  col.align === 'right' ? 'text-right' : 'text-left'
                )}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={rowKey(row, i)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                'border-b border-grey-200 dark:border-borderPrimary-dark text-textPrimary dark:text-textPrimary-dark',
                onRowClick && 'cursor-pointer hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark transition-colors',
                rowClassName?.(row)
              )}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cn(
                    'px-3 py-2 align-top',
                    col.align === 'right' ? 'text-right' : 'text-left',
                    col.cellClassName?.(row)
                  )}
                >
                  {col.render(row, i)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
    </div>
  );
}
